import { ChangeEvent, useState } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import PersonalDetails from "./PersonalDetails";
import Resume from "./Resume";
import { ApiError, applyJob, Job } from "services";
import { useTenant } from "tenant";
import { RESUME_MAX_BYTES, RESUME_MIME_TYPES } from "./resumeRules";
import { FormErrors, IPersonalDetails } from "./types";

const EMPTY_DETAILS: IPersonalDetails = {
  fullName: "",
  email: "",
  phone: "",
  lastCompany: "",
  location: "",
  linkedIn: "",
  portfolio: "",
  otherLink: "",
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]{5,15}$/;
const URL_PATTERN =
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

/** Why a resume file is not accepted, or null if it is fine. */
export function resumeError(file: File): string | null {
  if (!RESUME_MIME_TYPES.includes(file.type)) {
    return "Please upload a PDF, DOC or DOCX file.";
  }
  if (file.size > RESUME_MAX_BYTES) {
    return `The file must be smaller than ${RESUME_MAX_BYTES / (1024 * 1024)} MB.`;
  }
  return null;
}

function submitErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return "This role is no longer accepting applications.";
    }
    if (error.status === 400 || error.status === 413) {
      return error.message;
    }
  }
  return "We couldn't send your application. Please check your connection and try again.";
}

type ApplyFormProps = { job: Job };

const ApplyForm = ({ job }: ApplyFormProps) => {
  const { slug, company } = useTenant();
  const [step, setStep] = useState<1 | 2>(1);
  const [details, setDetails] = useState<IPersonalDetails>(EMPTY_DETAILS);
  const [resume, setResume] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTo, setSubmittedTo] = useState<string | null>(null);

  const clearError = (key: keyof FormErrors) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const handleDetails = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
    clearError(name as keyof FormErrors);
  };

  // A rejected or removed file leaves no resume selected, so the old one is
  // never submitted by mistake.
  const onFileSelected = (file: File | null) => {
    if (!file) {
      setResume(null);
      clearError("resume");
      return;
    }
    const problem = resumeError(file);
    if (problem) {
      setResume(null);
      setErrors((prev) => ({ ...prev, resume: problem }));
      return;
    }
    setResume(file);
    clearError("resume");
  };

  const validateStep1 = (): boolean => {
    const next: FormErrors = {};
    if (!details.fullName.trim()) next.fullName = "Please enter your full name.";
    if (!details.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL.test(details.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!details.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!PHONE.test(details.phone.trim()))
      next.phone = "Please enter a valid phone number.";
    if (!details.location.trim()) next.location = "Please tell us where you are based.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateStep2 = (): boolean => {
    const next: FormErrors = {};
    if (!resume) next.resume = "Please add your resume.";
    for (const key of ["linkedIn", "portfolio", "otherLink"] as const) {
      const value = details[key].trim();
      if (value && !URL_PATTERN.test(value)) next[key] = "Please enter a valid link.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2() || !resume) return;

    setIsSubmitting(true);
    const formData = new FormData();
    for (const [key, value] of Object.entries(details)) {
      if (value.trim()) formData.append(key, value.trim());
    }
    formData.append("resume", resume);

    try {
      await applyJob(slug, job.system.id, formData);
      setSubmittedTo(details.email.trim());
    } catch (error) {
      setErrors({ submit: submitErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submittedTo) {
    return (
      <section
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-4 rounded-xl bg-white p-8 text-center shadow-sm"
      >
        <CheckCircleIcon className="h-12 w-12 text-green-500" aria-hidden="true" />
        <h2 className="text-2xl font-semibold text-gray-900">Application sent</h2>
        <p className="text-gray-700">
          Thank you for applying for {job.details.jobTitle}
          {company.name ? ` at ${company.name}` : ""}. We'll send a confirmation
          to {submittedTo}, and the team will be in touch if your profile is a
          match.
        </p>
        <Link
          to="/"
          className="rounded-lg border border-orange-200 px-4 py-2 font-medium text-orange-700 hover:bg-orange-50"
        >
          See other open roles
        </Link>
      </section>
    );
  }

  return (
    <div className="rounded-xl bg-white p-6 shadow-sm">
      <p className="mb-4 text-sm text-gray-500">Step {step} of 2</p>
      {errors.submit && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-100 bg-red-50 p-3 text-red-700"
        >
          {errors.submit}
        </div>
      )}

      {step === 1 ? (
        <PersonalDetails
          onNext={() => validateStep1() && setStep(2)}
          handlePersonalDetails={handleDetails}
          personalDetails={details}
          errors={errors}
        />
      ) : (
        <Resume
          onPrev={() => setStep(1)}
          handlePersonalDetails={handleDetails}
          personalDetails={details}
          resume={resume}
          onFileSelected={onFileSelected}
          handleSubmit={handleSubmit}
          errors={errors}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default ApplyForm;
